// src/components/engineers/EngineerStatusUpdateModal.jsx
import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";

const statusStyles = {
  Active: { bgcolor: "#8CD47E", color: "#fff", borderColor: "#8CD47E" },
  APPROVED: { bgcolor: "#8CD47E", color: "#fff", borderColor: "#8CD47E" },
  Pending: { bgcolor: "#F8D66D", color: "#fff", borderColor: "#F8D66D" },
  PENDING: { bgcolor: "#F8D66D", color: "#fff", borderColor: "#F8D66D" },
  Rejected: { bgcolor: "#FF6961", color: "#fff", borderColor: "#FF6961" },
  REJECTED: { bgcolor: "#FF6961", color: "#fff", borderColor: "#FF6961" },
};

// Helper function to get possible status transitions
const getPossibleTransitions = (currentStatus) => {
  const transitions = {
    Pending: ["Active", "Rejected"],
    PENDING: ["Active", "Rejected"],
    Active: ["Rejected"],
    APPROVED: ["Rejected"],
    Rejected: ["Active"],
    REJECTED: ["Active"]
  };
  
  // Convert backend status to frontend status for logic
  const statusMap = {
    'APPROVED': 'Active',
    'PENDING': 'Pending',
    'REJECTED': 'Rejected'
  };
  
  const frontendStatus = statusMap[currentStatus] || currentStatus;
  return transitions[frontendStatus] || [];
};

// Get frontend status display
const getFrontendStatus = (backendStatus) => {
  switch (backendStatus) {
    case 'APPROVED':
      return 'Active';
    case 'PENDING':
      return 'Pending';
    case 'REJECTED':
      return 'Rejected';
    default:
      return backendStatus || 'Pending';
  }
};

// Get backend status from frontend
const getBackendStatus = (frontendStatus) => {
  switch (frontendStatus) {
    case 'Active':
      return 'APPROVED';
    case 'Pending':
      return 'PENDING';
    case 'Rejected':
      return 'REJECTED';
    default:
      return frontendStatus?.toUpperCase() || 'PENDING';
  }
};

// Function to get project counts
const getProjectCounts = (engineer) => {
  if (!engineer) {
    return {
      completed: 0,
      assigned: 0,
      display: "0/0",
      tooltip: "Completed: 0\nAssigned: 0"
    };
  }

  let completed = 0;
  let assigned = 0;

  if (engineer.projectsRecords) {
    completed = engineer.projectsRecords.projectCompleted || 0;
    assigned = engineer.projectsRecords.projectAssigned || 0;
  }
  
  return {
    completed,
    assigned,
    display: `${completed}/${assigned}`,
    tooltip: `Completed: ${completed}\nAssigned: ${assigned}`
  };
};

// Function to get location
const getLocation = (engineer) => {
  if (!engineer) return "-";
  
  const locationParts = [];
  if (engineer.city) locationParts.push(engineer.city);
  if (engineer.state) locationParts.push(engineer.state);
  
  if (locationParts.length > 0) {
    return locationParts.join(', ');
  }
  
  return engineer.detailedAddress || "-";
};

const EngineerStatusUpdateModal = ({
  open,
  onClose,
  engineer,
  onUpdate, // Changed from onApprove/onReject to onUpdate
}) => {
  // Get current status and possible transitions
  const currentStatus = getFrontendStatus(engineer?.status || 'PENDING');
  const possibleTransitions = useMemo(() => 
    getPossibleTransitions(engineer?.status || 'PENDING'), [engineer]
  );
  
  // Initialize selectedStatus - only set once based on valid options
  const [selectedStatus, setSelectedStatus] = useState(() => {
    // Default to first available option
    return possibleTransitions[0] || currentStatus;
  });
  
  // Get project info
  const projectInfo = useMemo(() => 
    getProjectCounts(engineer), [engineer]
  );

  // Get location
  const location = useMemo(() => 
    getLocation(engineer), [engineer]
  );

  // Handle status button click - with toggle functionality
  const handleStatusClick = (status) => {
    // Toggle: if clicking the already selected status, deselect it
    if (selectedStatus === status) {
      setSelectedStatus(null); // Deselect
    } else {
      setSelectedStatus(status); // Select new status
    }
  };

  // Handle update with selected status
  const handleUpdate = () => {
    if (onUpdate && engineer && selectedStatus) {
      onUpdate(selectedStatus);
    }
  };

  // Early return after all hooks
  if (!open || !engineer) return null;

  return (
    <>
      {/* Overlay */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          zIndex: 1400,
        }}
      />

      {/* Modal */}
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 700,
          bgcolor: "#fff",
          borderRadius: 2,
          p: 3,
          zIndex: 1401,
          boxShadow: "0px 12px 30px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
            Engineer Status Update
          </Typography>

          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <Icon name="close" color={COLORS.ERROR} />
          </IconButton>
        </Box>

        {/* Status Change Section */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1.5 }}>
            Current status: <strong>{currentStatus}</strong>
          </Typography>
          
          <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY, mb: 1.5 }}>
            Change to:
          </Typography>
          
          <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
            {possibleTransitions.map((status) => {
              const isSelected = selectedStatus === status;
              const statusStyle = statusStyles[status];
              
              return (
                <Button
                  key={status}
                  variant={isSelected ? "contained" : "outlined"}
                  onClick={() => handleStatusClick(status)}
                  sx={{
                    textTransform: "none",
                    minWidth: 100,
                    borderColor: statusStyle?.borderColor || COLORS.PRIMARY,
                    bgcolor: isSelected ? statusStyle?.bgcolor : "transparent",
                    color: isSelected ? "#fff" : statusStyle?.borderColor,
                    '&:hover': {
                      bgcolor: isSelected 
                        ? statusStyle?.bgcolor 
                        : statusStyle?.borderColor + "10",
                      borderColor: statusStyle?.borderColor,
                      color: isSelected ? "#fff" : statusStyle?.borderColor,
                    }
                  }}
                >
                  {status}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Engineer Info */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            {/* LEFT COLUMN */}
            <Box>
              {/* Name + Status */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Icon name="profile" color={COLORS.PRIMARY} />
                <Typography sx={{...TYPOGRAPHY.H3 ,color: COLORS.TEXT}}>
                  {engineer.name}
                </Typography>

                <Chip
                  label={currentStatus}
                  size="small"
                  sx={{ 
                    ...(statusStyles[engineer.status] || statusStyles.Pending), 
                    height: 20,
                    ...TYPOGRAPHY.BODY_SMALL 
                  }}
                />

                <Icon
                  name="arrowRight"
                  size={16}
                  stroke={2}
                  color={COLORS.PRIMARY}
                  sx={{
                    mx: 1.5,
                    transform: "scaleX(10)",
                  }}
                />
                
                {/* Show selected status chip only if a status is selected */}
                {selectedStatus ? (
                  <Chip
                    label={selectedStatus}
                    size="small"
                    sx={{ 
                      ...statusStyles[selectedStatus], 
                      height: 20,
                      ...TYPOGRAPHY.BODY_SMALL  
                    }}
                  />
                ) : (
                  <Chip
                    label="Not selected"
                    size="small"
                    sx={{ 
                      bgcolor: "#f0f0f0", 
                      color: "#888", 
                      height: 20,
                      ...TYPOGRAPHY.BODY_SMALL  
                    }}
                  />
                )}
              </Box>

              {/* Email */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1,pl:"24px" }}>
                <Icon name="at" size={14} color={COLORS.SECONDARY} />
                <Typography sx={{...TYPOGRAPHY.BODY_STANDARD,color: COLORS.SECONDARY}}>
                  {engineer.email}
                </Typography>
              </Box>

              {/* Phone */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 ,pl:"24px"}}>
                <Icon name="phoneDone" size={14} color={COLORS.SECONDARY} />
                <Typography sx={{...TYPOGRAPHY.BODY_STANDARD,color: COLORS.SECONDARY}}>
                  {engineer.phone}
                </Typography>
              </Box>
            </Box>

            {/* RIGHT COLUMN (ROW ALIGNED) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              {/* Row 1 — aligns with Name */}
              <Typography sx={{...TYPOGRAPHY.BODY_LARGE,color: COLORS.TEXT}}>
                Joined on:{" "}
                {new Date(
                  engineer.joinedAt || engineer.createdAt
                ).toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
}).replace(/\//g, '/')}
              </Typography>

              {/* Row 2 — spacer for Email */}
              <Box sx={{ height: 30 }} />

              {/* Row 3 — aligns with Phone */}
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.SECONDARY,
                  whiteSpace: "nowrap",
                }}
              >
                Projects: {projectInfo.display}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`, my: 2 }} />

        {/* Confirmation Text - Updated to handle no selection */}
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, mb: 3 , color: COLORS.TEXT}}>
          {selectedStatus ? (
            <>
              Click "Update Status" to change status from{" "}
              <strong>"{currentStatus}"</strong> to{" "}
              <strong>"{selectedStatus}"</strong> for{" "}
              {engineer.name}
            </>
          ) : (
            <>
              Please select a status to update from{" "}
              <strong>"{currentStatus}"</strong> for {engineer.name}.
            </>
          )}
        </Typography>

        {/* Actions - Disable Approve button if no status selected */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Icon name="close2" />}
            onClick={onClose}
            sx={{ 
              textTransform: "none",
              borderColor: COLORS.ERROR,
              color: COLORS.ERROR
            }}
          >
          Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={<Icon name="check" />}
            sx={{ 
              bgcolor: COLORS.PRIMARY, 
              textTransform: "none",
              opacity: selectedStatus ? 1 : 0.5,
              '&:disabled': {
                bgcolor: COLORS.PRIMARY,
              }
            }}
            onClick={handleUpdate}
            disabled={!selectedStatus}
          >
            Update Status
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default EngineerStatusUpdateModal;